/**
 * English is the source of truth: `Strings = typeof en`, so uz.ts and ru.ts must
 * supply every key with the same shape or the build fails. Copy that needs a
 * value goes in as a function so the call site stays typed.
 */
export const en = {
  nav: {
    how: "How it works",
    industries: "Industries",
    pricing: "Pricing",
    faq: "FAQ",
    signIn: "Sign in",
    startFree: "Start free",
    openApp: "Open OctoMail",
    language: "Language",
  },
  landing: {
    hero: {
      eyebrow: "Your always-on assistant for new customers",
      h1: "Every customer, answered.",
      lead: "OctoMail reads every new message in your email and Telegram, replies within a minute in your voice and in the customer's language, and hands you the real buyers with their details already collected.",
      ctaTrial: "Start your 7-day free trial",
      ctaSee: "See it work",
      fine: "No setup fee · Cancel anytime · Works with Gmail and Telegram",
    },
    demo: {
      email: "Email",
      telegram: "Telegram",
      aria: "Example conversation",
      replied: "OctoMail replied in under a minute",
      qualified: "Qualified lead",
      emailConvo: {
        name: "Jordan Avery",
        tag: "Used vehicle",
        customer1: "Hi, is the 2021 F-150 XLT still available? Could I see it this weekend?",
        reply: "Hi Jordan! Yes, the 2021 F-150 XLT is still available. We'll confirm a viewing time with you: are Saturday or Sunday mornings easier?",
        customer2: "Saturday morning works.",
      },
      telegramConvo: {
        name: "Emma",
        tag: "Bathroom",
        customer1: "Hi! How much would it cost to redo our bathroom?",
        reply: "Hi Emma! Thanks for reaching out. We'll share pricing once we know the details: roughly how big is the bathroom, and when would you like to start?",
        customer2: "About 60 sq ft. Next month.",
      },
    },
    does: {
      eyebrow: "What it does",
      title: "What OctoMail does for you",
      items: [
        { title: "Reads every message", body: "Email and Telegram, day and night, the second it arrives." },
        { title: "Replies within a minute", body: "In your voice and in the customer's language: English, Uzbek, Russian and more." },
        { title: "Collects the details", body: "Asks for what you need, like the address, timing or budget, so every lead arrives ready." },
        { title: "Hands you what matters", body: "Complaints and existing customers go straight to you, with a heads-up." },
      ],
    },
    day: {
      eyebrow: "See it work",
      title: "From first message to a ready lead, while you work",
      steps: [
        { time: "9:41", title: "A customer writes", message: "Hi, we need our kitchen redone. We're in Maple Ridge.", note: "By email, while you're on a job." },
        { time: "9:42", title: "OctoMail answers", message: "Hi Dana! Thanks for reaching out. When would you like to start, and roughly how big is the kitchen?", note: "In your voice. No prices, no promises." },
        { time: "9:50", title: "You get a ready lead", message: "", note: "You call back the ones worth your time." },
      ],
      lead: [
        { k: "Job", v: "Kitchen remodel" },
        { k: "Area", v: "Maple Ridge" },
        { k: "Start", v: "Next month" },
      ],
      statusLabel: "Status",
      status: "Qualified",
    },
    control: {
      eyebrow: "You stay in control",
      title: "It works for you, never instead of you",
      items: [
        "Email: you approve the very first reply, then it runs on its own.",
        "Telegram: you choose which chats it can answer.",
        "The moment you reply yourself, OctoMail steps back in that conversation.",
        "It never quotes prices, gives discounts or promises dates. Complaints and existing customers come straight to you.",
      ],
    },
    industries: {
      eyebrow: "Built for the way you get leads",
      title: "Whatever you sell, the first reply wins.",
      sub: "Real inquiries, answered in your voice, automatically. Here's what that looks like in your world.",
      inbound: "Inbound",
      inYourVoice: "OctoMail · in your voice",
      seeFor: (x: string) => `See OctoMail for ${x} →`,
      items: {
        dealerships: {
          label: "Dealerships",
          inbound: "Is the 2021 F-150 XLT still available?",
          reply: "Hi Jordan, yes, it's still on the lot. Want to set up a time to come see it?",
          linkText: "dealerships",
        },
        contractors: {
          label: "Contractors",
          inbound: "Looking for a quote to redo our kitchen. When could you look?",
          reply: "Hi Dana, I'd love to take a look. Are mornings or afternoons better to measure up?",
          linkText: "contractors",
        },
        dental: {
          label: "Dental offices",
          inbound: "Do you take new patients? Need a cleaning and a check-up.",
          reply: "Hi Sam, yes, we'd be glad to get you in. What days generally work best for you?",
          linkText: "dental offices",
        },
        auto: {
          label: "Auto service",
          inbound: "Brakes are squealing. Can you take a look this week?",
          reply: "Hi Alex, we can absolutely take a look. Is the car drivable, and which day's easiest for you?",
          linkText: "auto shops",
        },
        rv: {
          label: "RV stores",
          inbound: "Is the 2022 Winnebago travel trailer still listed?",
          reply: "Hi Pat, yes, that one's still available. Want to come by for a walk-through?",
          linkText: "RV dealers",
        },
        sales: {
          label: "Sales teams",
          inbound: "Evaluating tools for a team of 12. Can you tell me more?",
          reply: "Hi Riley, happy to walk you and the team through it. What's the best way to set up a quick call?",
          linkText: "sales teams",
        },
      },
    },
    stats: {
      eyebrow: "Speed wins work",
      title: "The business that answers first usually wins.",
      items: [
        { big: "21×", small: "more likely to win the job when you reply within 5 minutes" },
        { big: "78%", small: "of buyers hire the first business that responds" },
        { big: "< 1 min", small: "for OctoMail to answer a new inquiry, day or night" },
      ],
      note: "Lead-response figures from industry research (Lead Response Management / Harvard Business Review). Now onboarding our first businesses in Canada, the US and Uzbekistan. Join the founding group.",
    },
    pricing: {
      eyebrow: "Pricing",
      title: "One simple plan. No surprises.",
      sub: "Less than what you'd lose from a single missed job.",
      monthly: "Monthly",
      annual: "Annual · save 25%",
      planName: "OctoMail",
      planSub: "Everything you need to never miss a lead.",
      perMo: (code: string) => `/mo ${code}`,
      billedYearly: (annual: string, code: string) => `Billed ${annual} ${code} per year`,
      billedMonthly: (code: string) => `Billed monthly in ${code}`,
      switchNote: " · switch to annual for 3 months free",
      factDay: (rate: string) => `About ${rate} a day`,
      factAdmin: "A part-time admin runs $20,000+/yr",
      factPays: "Pays for itself with one job",
      features: [
        "Reads every new email and Telegram message",
        "Sorts real buyers from junk",
        "Replies in your voice, in the customer's language",
        "Works with Gmail and Telegram",
        "Every conversation in one place",
      ],
      telegramNote: "Telegram connection requires Telegram Premium on the business account.",
      cta: "Start your 7-day free trial",
      fine: "Free for 7 days. No card charged until you keep it.",
    },
    faq: {
      eyebrow: "Questions",
      title: "Good questions, straight answers.",
      items: [
        { q: "Do I need to be good with computers?", a: "No. You sign in with Google once, and OctoMail does the rest on its own. If you can check your email, you can use it." },
        { q: "How does it tell a real job from junk?", a: "OctoMail reads each email against the things you care about: who they are, what they need, the timeline, the location. It flags genuine inquiries and sets spam and tire-kickers aside, so a glance tells you who's worth following up with." },
        { q: "What if a customer leaves out the budget or timeline?", a: "OctoMail marks the lead as “needs info” and drafts a friendly reply asking only for the missing details, so the conversation keeps moving without you having to chase it." },
        { q: "Does it reply on its own?", a: "Yes, that's the point. On email, OctoMail replies to new leads automatically in your voice after you approve your very first reply. On Telegram it answers new customers in the chats you allow as soon as you connect. It never quotes a price or commits to a date, and you can turn it off anytime." },
        { q: "Will my customers know it's automated?", a: "No. Replies are warm, written in plain language in your voice, never robotic templates. On email you see and approve the first one before anything goes out." },
        { q: "Does it work with my email?", a: "OctoMail works with Gmail and Google Workspace today. On Outlook or another provider? Email support@octoaiverse.com and we'll tell you the moment we support it." },
        { q: "Is my email safe?", a: "Yes. We connect through Google's official secure sign-in. Your password never touches OctoMail and your emails are never shared." },
        { q: "How much does it cost?", a: "$99/month USD ($129 CAD), or save with annual billing. Every account starts with a 7-day free trial. No card is charged until you decide to keep it." },
        { q: "What if I want to cancel?", a: "Cancel anytime in two clicks, or email support@octoaiverse.com and we'll take care of it. No hoops." },
        { q: "Does it work with Telegram?", a: "Yes. You connect OctoMail to your Telegram account once, in Telegram's Business settings under Chatbots. Your customers keep writing to you as usual, not to a bot, and OctoMail answers in the chats you allow. Telegram requires Telegram Premium on the business account for this." },
        { q: "What happens to my customers' messages?", a: "OctoMail reads them only to understand each inquiry and write a reply; the text is processed by Google Gemini for that. Conversations are kept while your account is active and are erased when you delete your account. Telegram chats are also deleted 30 days after you disconnect." },
      ],
    },
    footer: {
      tagline: "Every customer, answered. OctoMail replies to your leads on email and Telegram while you're on the job.",
      socialAria: (label: string) => `OctoAIverse on ${label}`,
      product: "Product",
      industries: "Industries",
      answers: "Answers",
      company: "Company",
      links: {
        contractors: "Contractors",
        dealerships: "Car dealerships",
        dental: "Dental offices",
        auto: "Auto repair",
        rv: "RV dealers",
        sales: "Sales teams",
        speedToLead: "What is speed to lead",
        howFast: "How fast to respond",
        vsReceptionist: "OctoMail vs a receptionist",
        canAi: "Can AI answer email",
        contact: "Contact",
        privacy: "Privacy",
        terms: "Terms",
      },
      copyrightA: (year: number) => `© ${year} OctoMail by `,
      copyrightB: ". All rights reserved.",
    },
  },
  app: {
    loading: "Loading",
    nav: { home: "Home", leads: "Leads", questions: "Questions", settings: "Settings", help: "Help" },
    shell: { account: "Account", signOut: "Sign out", copyright: (year: number) => `© ${year} OctoMail by OctoAIverse` },
    fmt: { justNow: "just now", minAgo: (m: number) => `${m}m ago`, hrAgo: (h: number) => `${h}h ago`, dayAgo: (d: number) => `${d}d ago`, today: "Today", yesterday: "Yesterday", generic: "Something went wrong." },
    status: { qualified: "Qualified", needsInfo: "Needs info", unqualified: "Unqualified", held: "Held", pending: "Queued" },
    filters: { filters: "Filters", status: "Status", all: "All", qualified: "Qualified", needsInfo: "Needs info", category: "Category", clearAll: "Clear all" },
    thread: {
      noHistory: "No conversation history yet. This lead may have been qualified before message tracking started.",
      reply: "Reply", edit: "Edit", preview: "Preview", copied: "Copied!", copy: "Copy",
      placeholder: "Edit the AI's draft and hit Send, or type your own…", replyAria: "Reply body",
      closedNote: "This lead is closed. Reopen it below to send another reply.",
      supportsA: "Supports", bold: "**bold**", italic: "*italic*", supportsB: ", lists, and links. Your customer sees a clean, formatted email.",
      sending: "Sending…", sendTo: (name: string) => `Send to ${name}`, resetDraft: "Reset to AI draft",
      replyWindow: (h: number, m: number) => `Telegram lets you reply here for ${h}h ${m}m more.`,
      windowClosed: "Telegram closed this chat to OctoMail (24 hours without a customer message). Reply from Telegram instead.",
      openInTelegram: "Open in Telegram",
      telegramHint: "Sent from your Telegram account as a plain message. Formatting is removed.",
      sendErrors: {
        reply_window_closed: "Telegram closed this chat to OctoMail (24 hours without a customer message). Reply from Telegram instead.",
        telegram_not_connected: "Telegram is not connected. Reconnect it in Settings.",
        telegram_cannot_reply: "Telegram does not allow OctoMail to reply. In Telegram: Settings > Telegram Business > Chatbots, turn on replying under Manage Messages.",
        send_failed: "Telegram did not accept this message. Try again, or reply from Telegram.",
        subscription_required: "Replying needs an active OctoMail plan or trial. Check Plan & billing in Settings.",
        body_too_long: "This message is too long for Telegram (4096 characters max).",
        body_required: "Write a message first.",
      },
    },
    msg: { sentOnYourBehalf: "sent on your behalf", openInGmail: "Open in Gmail →" },
    questions: {
      title: "Questions", subtitle: "What makes a lead worth your time. OctoMail looks for these in every quote request.",
      add: "Add question", loadingAria: "Loading questions",
      emptyTitle: "Tell OctoMail what makes a good lead",
      emptyBody: "OctoMail checks every inbound quote request against these questions. If required answers are missing, it flags or follows up automatically.",
      adding: "Adding…", starterPack: "Use the 6-question starter pack", scratch: "Start from scratch", saveChanges: "Save changes",
      moveUp: "Move up", moveDown: "Move down", edit: "Edit", editQuestion: "Edit question", delete: "Delete", deleteQuestion: "Delete question",
      required: "Required", optional: "Optional",
      kinds: { text: "Text", select: "Choice", number: "Number", boolean: "Yes / No" },
      confirmDelete: "Delete this question? This cannot be undone.", failDelete: "Failed to delete.", failReorder: "Failed to reorder.", failSeed: "Failed to seed templates.",
      editor: {
        labelLabel: "Question text", labelPlaceholder: "e.g. What's the project address?", kindLabel: "Answer type",
        hints: { text: "Free-form answer (e.g. address, notes)", select: "Pick one from a fixed list (e.g. budget range)", number: "Numeric value (e.g. square feet)", boolean: "Single yes-or-no question" },
        requiredLabel: "Required for qualification", optionsLabel: "Options", optionsHint: "(one per line)",
        optionsPlaceholder: "Under $5,000\n$5,000 to $25,000\n$25,000 to $100,000\nOver $100,000",
        optionsNote: "At least 2 options. The lead's answer must match one of these (case-insensitive) to be considered valid.",
        errLabelRequired: "Question label is required.", errLabelLong: "Question label must be 200 characters or fewer.", errOptions: "Choice questions need at least 2 options.",
        failSave: "Failed to save.", saving: "Saving…", cancel: "Cancel",
      },
    },
    starter: [
      { label: "What type of project is this?", options: ["Kitchen remodel", "Bathroom remodel", "Basement / addition", "Exterior / roofing", "Other"] },
      { label: "What's the project address?" },
      { label: "What's the budget range?", options: ["Under $5,000", "$5,000 to $25,000", "$25,000 to $100,000", "Over $100,000"] },
      { label: "When do you need this completed?" },
      { label: "Do you have plans or measurements ready?" },
      { label: "Best way to reach you?", options: ["Email", "Phone", "Text"] },
    ],
    settings: {
      title: "Settings", subtitle: "Personal preferences. These never leave your account.",
      billing: {
        name: "Plan & billing", hint: "Your subscription, trial, and payment details.", loading: "Loading…",
        activeRenews: (d: string) => `Active, renews ${d}`, trialLeft: (n: number) => `Free trial, ${n} days left`, pastDue: "Payment failed, please update your card", canceled: "Canceled", noPlan: "No active plan",
        active: "Active", trialChip: (n: number) => `Free trial · ${n} days left`, paymentFailed: "Payment failed",
        subscribed: "You're subscribed. Thank you!", cancelled: "Checkout cancelled. No charge was made.",
        perMonth: "month", perYear: "year", annualNote: "Three months free compared to monthly.", monthlyNote: "Cancel anytime. Two clicks, no hoops.",
        monthly: "Monthly", annual: "Annual",
        features: ["Unlimited inbound leads", "AI replies in your voice", "Autopilot with approval control", "Daily briefing & lead pipeline"],
        redirecting: "Redirecting…", subscribe: "Subscribe", loadingPortal: "Loading portal…", manage: "Manage billing",
      },
      autopilot: {
        name: "Autopilot", hint: "OctoMail replies to new leads automatically, in your voice. It never quotes a price or commits to a date on its own.",
        title: "Automatic replies to new leads", body: "You approve the very first reply so you can see the quality. After that it runs on its own.",
        on: "On. New leads get an automatic reply.", off: "Off. You'll review and send every reply yourself.", failed: "Couldn't update Autopilot.",
      },
      cc: {
        name: "Auto-Cc replies", hint: "Copy a second address (your assistant or partner) on every reply OctoMail sends.", legend: "Auto-Cc replies",
        secondary: "Secondary email", placeholder: "assistant@yourcompany.com", ccLabel: "Cc this address on every reply", ccHint: "Recipients will see the Cc'd address.",
        errNeedEmail: "Add a secondary email or turn off auto-Cc.", errInvalid: "That doesn't look like a valid email.", failSave: "Failed to save.",
        saving: "Saving…", save: "Save settings", saved: "Saved ✓",
      },
      telegram: {
        name: "Telegram",
        hint: "Let OctoMail answer customers who write to your Telegram. Needs Telegram Premium.",
        consentTitle: "Before you connect",
        consentBody: [
          "OctoMail reads only the chats you share with it in Telegram (Settings > Telegram Business > Chatbots).",
          "Message text from those chats is sent to Google Gemini to understand them and write replies.",
          "We keep those chats while your account is active. You can delete your account at any time, which erases everything, and Telegram chats are deleted 30 days after you disconnect.",
          "OctoMail never quotes prices or promises dates, and it stops in any chat you answer yourself.",
        ],
        consentCheck: "I agree",
        connect: "Connect Telegram",
        connecting: "Opening Telegram…",
        waiting: "Tap Start in Telegram, then follow the bot's steps. This page updates by itself.",
        linkedNotConnected: "Linked. Last step in Telegram: Settings > Telegram Business > Chatbots > add @OctoAIversebot.",
        connected: (u: string) => `Connected${u ? ` as @${u}` : ""}. New customer chats get an automatic reply.`,
        cannotReply: "Telegram does not allow OctoMail to reply. In Telegram: Settings > Telegram Business > Chatbots, turn on replying under Manage Messages.",
        deleteAllWarning: "Tip: in Chatbots, switch off \"delete all messages\" under Manage Messages. OctoMail never needs it.",
        setupTips: [
          "Choose \"All Private Chats Except...\" and exclude \"Contacts\", so friends and family stay private.",
          "Your customers keep writing to YOU, not to the bot.",
        ],
        usage: (n: number, max: number) => `${n} of ${max} AI conversations this month`,
        failed: "Couldn't start the Telegram connection.",
        startOver: "Start over",
        reloadPage: "OctoMail was updated. Reload this page and try again.",
        needsSubscription: "Telegram needs an active OctoMail plan or trial. Check Plan & billing in Settings.",
      },
      more: {
        name: "More from OctoAIverse", hint: "Your messages are covered. What about your phone line?", product: "OctoReception", tagline: "Every call, answered.",
        body: "An AI receptionist that picks up your business line 24/7: answers questions, books appointments, and texts you a summary the moment the call ends.",
        cta: "Try a free test call →", price: "From $24.95 USD/mo · no card needed to try",
      },
      danger: {
        name: "Danger zone", hint: "Permanently delete your account, your data, and OctoMail's access to your inbox.",
        body1A: "Deleting your OctoMail account erases everything stored here (leads, message history, settings), cancels any active subscription, and disconnects OctoMail from your inbox.", body1B: "This can't be undone.",
        body2: "Your accounts themselves are never touched: your Gmail and Telegram accounts stay exactly as they are, and every message, including replies OctoMail sent for you, stays where it is. We simply lose access.",
        delete: "Delete account", confirm: "Are you sure? This permanently erases everything and can't be undone.", deleting: "Deleting…", yes: "Yes, delete everything", cancel: "Cancel",
        fail: "Couldn't delete your account. Please try again.",
      },
    },
    help: {
      title: "Help & support", subtitle: "Stuck on something? A real person will help.", talk: "Talk to a human", sameDay: "We usually reply the same day.", emailSupport: "Email support",
      cantFindA: "Can't find what you need? Email", cantFindB: "and a real person reads every message.",
      sections: [
        { title: "Getting started", faqs: [
          { q: "How fast does OctoMail reply to a new lead?", a: "OctoMail reads a new inquiry within seconds of it landing in your inbox and has a reply drafted right away. With Autopilot on, it sends automatically, typically inside a minute, long before a competitor has even opened the email." },
          { q: "Do I need any technical setup?", a: "No. You sign in once with Google or your email, connect Gmail or Telegram, and OctoMail does the rest: no forwarding rules, no IT, nothing to install. If you can check your messages, you can use it." },
          { q: "Can I use OctoMail with Telegram only?", a: "Yes. During setup, connect Telegram instead of Gmail (it needs Telegram Premium). You can add Gmail later from Home whenever you like." },
          { q: "How does it decide what counts as a good lead?", a: "It reads each email against the things you care about (who they are, what they need, the timeline, the location) and flags genuine inquiries while setting spam and tire-kickers aside. You tune this anytime on the Questions page." },
          { q: "What if a lead doesn't include enough information?", a: "OctoMail marks the lead as “needs info” and drafts a friendly reply asking only for the missing details, so the conversation keeps moving without you having to chase it." },
        ] },
        { title: "Replies & Autopilot", faqs: [
          { q: "Will it send emails without me?", a: "Only if you want it to. You approve the very first reply so you can see how it sounds. After that, Autopilot sends automatically, and you can switch it off anytime in Settings to review every reply yourself." },
          { q: "Will it ever quote a price or promise a date?", a: "No. Replies are designed to never commit to a price, a date, or a scope you didn't set. It warms the lead up and hands the specifics to you." },
          { q: "Can replies copy my assistant or partner?", a: "Yes. Set a secondary address in Settings → Auto-Cc replies and every reply OctoMail sends will copy them." },
        ] },
        { title: "Billing", faqs: [
          { q: "How does the free trial work?", a: "Every new account starts with 7 days free: full product, no card required. Subscribe inside the app when you're ready; you won't be charged anything until you do." },
          { q: "How do I cancel?", a: "Two clicks in Settings → Manage billing, or email support@octoaiverse.com and we'll take care of it. No hoops, no retention scripts." },
          { q: "What currency am I charged in?", a: "Canadian customers are billed in CAD; everyone else in USD. The price you see in the app is the price you pay." },
        ] },
        { title: "Privacy & security", faqs: [
          { q: "Is my email safe?", a: "Yes. We connect through Google's official secure sign-in, so your password never touches OctoMail. Mailbox credentials are stored encrypted, and your emails are never shared or sold." },
          { q: "Can I disconnect my inbox?", a: "Anytime. Disconnecting in Settings immediately stops all access. Deleting your account also removes OctoMail from your Google account's connected apps." },
          { q: "Does OctoMail modify or delete my email?", a: "Never. It only reads new incoming mail to spot leads, and sends the replies you've approved. It can't label, archive, modify, or delete anything." },
        ] },
      ],
    },
    paywall: {
      title: "Your free trial has ended", sub: "Subscribe so OctoMail keeps collecting messages from your email and Telegram and replying to leads.",
      held: (n: number): string => n === 1 ? "1 lead arrived while your trial was paused" : `${n} leads arrived while your trial was paused`,
      heldNote: (n: number): string => n === 1 ? "It hasn't been read or answered yet. Subscribe and OctoMail will qualify it right away." : "They haven't been read or answered yet. Subscribe and OctoMail will qualify them right away.",
      monthly: "Monthly", annual: "Annual", save25: "save 25%", redirecting: "Redirecting…", subscribe: "Subscribe & keep my leads", signOut: "Sign out",
    },
    trial: {
      daysLeft: (n: number) => `${n} day${n === 1 ? "" : "s"} left in your free trial`,
      endsToday: "Your trial ends today",
    },
    home: {
      srTitle: "Home: your daily briefing",
      declined: "You declined access. Without inbox access, OctoMail can't read quote requests.",
      tryAgain: "Try again",
      noRefreshA: "Google didn't return a refresh token. Please disconnect this app from your",
      noRefreshLink: "Google account permissions",
      noRefreshB: "and try again.",
      connectError: "Something went wrong connecting your inbox. Please try again.",
      gmailConnected: (email: string) => `Gmail connected · ${email}`,
      gettingSetUp: "Getting set up",
      needsYou: "Needs you",
      caughtUp: "You're all caught up.",
      statNew: "New leads",
      statReplied: "Answered",
      statNeeds: "Need you",
      last7: "Leads · last 7 days",
      today: "Today",
      howItWorks: "How it works",
      handledBy: "Handled by Autopilot",
      pipelineIntro: "Here's what happens to every email from now on:",
      pipelineIntroTelegram: "Here's what happens to every new message from now on:",
      noRepliesYet: "No replies yet.",
      viewAllHandled: "View all handled leads →",
      viewAllNeeds: (n: number) => `View all ${n} in Leads →`,
      savedA: (mins: number) => `~${mins} minutes`,
      savedB: " saved on replies this week.",
      outcomes: "Reply outcomes",
      days30: "30 days",
      writeBack: (pct: number) => `${pct}% of leads write back`,
      responded: "Responded",
      waiting: "Waiting",
      cold: "Cold",
      ofWroteBack: (r: number, s: number) => `${r} of ${s} wrote back`,
      coldNote: "A lead counts as cold after 3 days with no reply back.",
      comingSoon: "Coming soon",
      roadmap: ["Outlook & Microsoft 365", "Calendar sync for booked jobs", "Multiple inboxes"],
      newLead: "New lead",
      channels: {
        aria: "Connected channels",
        gmail: "Gmail",
        telegram: "Telegram",
        email: "Email",
        connectGmail: "Connect Gmail",
        reconnectGmail: "Reconnect Gmail",
        connectTelegram: "Connect Telegram",
        telegramRepliesOff: "Telegram: replies off",
        telegramFinish: "Telegram: finish setup",
      },
      reasons: {
        complaint: "Complaint",
        existingCustomer: "Existing customer",
        media: "Voice or file",
        aiPaused: "AI paused",
        replySoon: "Reply soon",
        draftReady: "Draft ready",
        queued: "Queued",
        open: "Open",
      },
      lead: "Lead",
      replied: "Replied",
      leadCount: (n: number) => `${n} lead${n === 1 ? "" : "s"}`,
      autopilotRepliedIn: (speed: string) => `Autopilot replied ${speed} after the lead arrived`,
      there: "there",
      dow: ["S", "M", "T", "W", "T", "F", "S"],
      brief: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
        quiet: "Quiet so far, nothing needs you. I'll surface new leads here as they arrive.",
        repliedWeek: (n: number) => `You and Autopilot replied to ${n} lead${n === 1 ? "" : "s"} this week.`,
        noReplies: "No replies have gone out yet this week.",
        nothingNeeds: "Nothing needs you right now.",
        waitingOnYou: (n: number) => `${n} lead${n === 1 ? " is" : "s are"} waiting on you.`,
      },
      clock: { yourAssistant: "Your assistant", eyebrow: "Daily Briefing", live: "● live", currentTime: (t: string) => `Current time ${t}` },
      firstRun: {
        pipeline: [
          { title: "A lead emails you", sub: "Any inquiry that lands in your inbox." },
          { title: "OctoMail reads & scores it", sub: "Real buyers in, spam set aside." },
          { title: "A reply is drafted in your voice", sub: "Warm, personal, no invented prices." },
          { title: "You approve the first one", sub: "After that, Autopilot handles it." },
        ],
        pipelineTelegramFirst: { title: "A customer messages you", sub: "Any new chat in your Telegram." },
        pipelineTelegramLast: { title: "It answers right away", sub: "Only in chats you allow, and it stops once you reply yourself." },
        connectLabel: "Connect your Gmail",
        connectSub: "Done. OctoMail is watching your inbox.",
        tuneLabel: "Tune your qualification questions",
        tuneSub: "Tell it what a good lead looks like for your business.",
        tuneCta: "Review questions",
        testLabel: "Send yourself a test lead",
        testSub: "Email your own inbox a quote request and watch it get qualified.",
        testCta: "Send test email",
        tgConnectLabel: "Connect your Telegram",
        tgConnectSub: "Done. OctoMail answers new customer chats in Telegram.",
        tgFinishLabel: "Finish Telegram setup",
        tgFinishCta: "Open Telegram setup",
        tgTestLabel: "Try a test message",
        tgTestSub: "Ask someone who is not in your contacts to write to your Telegram, and watch OctoMail answer.",
        testSubject: "Quote request: kitchen renovation",
        testBody: "Hi! We're planning a kitchen renovation this fall and getting quotes this week. Budget is around $25,000 and we'd love to start in October. Could you send us a rough estimate and your availability?\n\nThanks,\nA. Customer",
      },
    },
    leads: {
      title: "Leads",
      subtitle: "Every inbound inquiry, scored against your questions.",
      loadingAria: "Loading leads",
      noLeadsYet: "No leads yet",
      noLeadsBody: "They'll appear here automatically as inbound emails arrive.",
      noOpen: "No open leads. You're all caught up.",
      noSnoozed: "Nothing snoozed right now.",
      noDone: "No completed leads yet.",
      noFiltered: "Nothing filtered out yet.",
      noMatch: "No leads match this filter.",
      notLeadBody: "Newsletters, receipts, and notifications land here, never your real leads.",
      tryAdjust: "Try adjusting the search, status, or category filters above.",
      firstRun: {
        eyebrow: "Waiting for your first lead",
        title: "Your leads will land here, already read, scored, and answered.",
        watchingA: "OctoMail is watching",
        yourInbox: "your inbox",
        yourTelegram: "your Telegram chats",
        andTelegram: " and your Telegram chats",
        yourChannels: "your connected channels",
        watchingB: ". The moment an inquiry arrives, it appears in this list with everything extracted: who they are, what they want, and the reply it drafted.",
        sendTest: "Send yourself a test lead",
        reviewQuestions: "Review your questions",
        testNote: "The test email arrives like any real inquiry, qualified in about a minute.",
        pipelineEyebrow: "What happens to every email",
        pipelineEyebrowMessages: "What happens to every message",
      },
      buckets: { attention: "Needs you", open: "Open", snoozed: "Snoozed", done: "Done", notlead: "Not leads" },
      noAttention: "Nothing needs you personally right now.",
      attentionBody: "Complaints and messages from existing customers land here. OctoMail never auto-replies to them.",
      attentionKind: { complaint: "Complaint", existing_customer: "Existing customer" },
      attentionBanner: "This message looks like it needs you personally. OctoMail did not reply automatically, so review the draft before sending anything.",
      pendingBanner: "OctoMail saved this email but couldn't read it yet. It retries automatically every 30 minutes.",
      bucketAria: "Lead bucket",
      searchPlaceholder: "Search leads…",
      searchAria: "Search leads",
      attachments: (n: number) => `${n} attachment${n === 1 ? "" : "s"}`,
      replied: "Replied",
      telegramBadge: "Telegram",
      mediaChip: "Voice or file",
      aiPausedCap: "AI paused: this month's limit is reached. Reply yourself; it resets on the 1st.",
      aiPausedDaily: "AI paused for today: daily safety limit reached.",
      panel: {
        back: "‹ Leads",
        close: "Close",
        noQuestionsA: "Add qualification questions and OctoMail will extract the answers (budget, timeline, location) from every lead.",
        setThemUp: "Set them up →",
        notProvided: "not provided",
        snooze: "Snooze",
        s1d: "1d",
        s3d: "3d",
        s1wk: "1wk",
        notInterested: "Not interested",
        unsnoozeUntil: (d: string) => `Unsnooze (until ${d})`,
        reopen: "Reopen",
      },
    },
    login: {
      homeAria: "Go to the OctoMail home page",
      welcomeBack: "Welcome back",
      createAccount: "Create your account",
      signInSub: "Sign in to OctoMail to manage your leads.",
      signUpSub: "Start qualifying your leads in minutes.",
      redirecting: "Redirecting…",
      continueGoogle: "Continue with Google",
      oneTap: "One tap connects your inbox too, no second step.",
      reviewA: "Google is finishing its review of OctoMail, so you may see a notice on the next screen. Choose",
      reviewAdvanced: "Advanced → Go to OctoMail",
      reviewB: "to continue. Your data stays private and secure.",
      or: "or",
      email: "Email",
      emailPlaceholder: "you@company.com",
      password: "Password",
      loading: "Loading…",
      signIn: "Sign in",
      createAccountBtn: "Create account",
      newTo: "New to OctoMail?",
      createAnAccount: "Create an account",
      already: "Already have an account?",
      agreeA: "By continuing you agree to our",
      terms: "Terms",
      and: "and",
      privacy: "Privacy Policy",
      agreeB: ".",
      errors: {
        badCredential: "Email or password is incorrect.",
        emailInUse: "An account with that email already exists.",
        weakPassword: "Password must be at least 8 characters.",
        cancelled: "Sign-in was cancelled.",
        network: "Network error. Check your connection and try again.",
        generic: "Something went wrong.",
      },
    },
    onboarding: {
      title: "Let's get you set up",
      subtitle: "Three quick steps and OctoMail starts working for you.",
      stepOf: (n: number, total: number) => `Step ${n} of ${total}`,
      s1Title: "Connect your channels",
      s1Body: "Choose where your customers reach you. One channel is enough to start, and you can add the other anytime.",
      gmailBody: "OctoMail reads incoming quote requests so it can sort and reply to them. Your password never leaves Google.",
      channelDone: "Connected",
      channelsLabel: "Channels",
      repliesOff: "Replies off",
      s1Busy: "Opening Google…",
      s1Btn: "Connect Gmail",
      s2Title: "Choose what makes a good lead",
      s2Body: "We'll start you with 6 common questions to ask (project type, address, budget, timeline…). You can edit them anytime.",
      s2Busy: "Adding…",
      s2Btn: "Use the starter pack",
      s2Manual: "Set up manually",
      s3Title: "You're all set",
      s3TitleAlmost: "Almost done",
      s3Body: "OctoMail is now watching your inbox. New quote requests will show up as leads automatically.",
      s3BodyTelegram: "OctoMail is now watching your Telegram chats. New customer messages will show up as leads automatically.",
      s3BodyBoth: "OctoMail is now watching your inbox and your Telegram chats. New requests will show up as leads automatically.",
      s3Btn: "Go to my leads",
    },
  },
  /** <head> metadata for the prerendered landing pages. */
  meta: {
    title: "OctoMail: every customer, answered",
    description: "OctoMail reads every new email and Telegram message, replies within a minute in your voice and your customer's language, and hands you the real buyers. Try it free for 7 days.",
    ogDescription: "Reads every new email and Telegram message, replies within a minute in your voice, and hands you the real buyers. For contractors, dealerships, dental, auto, RV, and sales teams.",
    ogImageAlt: "OctoMail: every customer, answered.",
  },
};

export type Strings = typeof en;
